// Buttons. One set of shapes for every screen: a 32 px pill (primary, outline,
// ghost, danger) and a 26 px round icon button. Hover changes colors only, never
// the size (borders exist from the start, transparent when not shown).
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "outline" | "ghost" | "danger";

const BASE =
  "box-border inline-flex h-8 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl border px-[13px] pb-px text-[13px] disabled:cursor-not-allowed disabled:opacity-50";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "border-accent bg-accent text-accent-fg hover:border-accent-hover hover:bg-accent-hover",
  outline: "border-border bg-surface text-body hover:border-field-strong",
  ghost: "border-transparent text-muted hover:bg-hover hover:text-body",
  danger: "border-danger bg-danger text-accent-fg hover:opacity-90",
};

export function buttonClass(variant: ButtonVariant = "outline"): string {
  return `${BASE} ${VARIANTS[variant]}`;
}

export function Button({
  variant = "outline",
  className = "",
  type = "button",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type={type}
      className={`${buttonClass(variant)} ${className}`}
      {...rest}
    />
  );
}

/** The round icon button's width and height, in px. */
export const ICON_BUTTON_SIZE = 26;

export const ICON_BUTTON =
  "inline-flex h-[26px] w-[26px] flex-none cursor-pointer items-center justify-center rounded-[13px] text-muted hover:bg-hover hover:text-body disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The small square icon button of a message's actions and the rows under it
 * (copy, react, the message's "…", side chat, undo, rewind): 24 px, 6 px
 * corners. The caller adds the icon's color (`text-muted` or `text-faint`).
 * `SMALL_ICON_BOX` is its size and corners alone, for one with hovers of its
 * own (a side chat that is open keeps the accent; undo sits on the hover
 * ground already).
 */
export const SMALL_ICON_BOX =
  "inline-flex h-6 w-6 flex-none items-center justify-center rounded-[6px]";
export const SMALL_ICON_BUTTON = `${SMALL_ICON_BOX} cursor-pointer hover:bg-hover hover:text-body disabled:cursor-not-allowed disabled:opacity-50`;

export function IconButton({
  label,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Read aloud and shown nowhere (no tooltip unless wrapped in <Tooltip>). */
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      aria-label={label}
      className={`${ICON_BUTTON} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
